import { Grid, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import { useCallback, useState } from 'react';

import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import { ReactComponent as Delete } from 'common/assets/images/delete.svg';
import { ReactComponent as GreenTick } from 'common/assets/images/green-tick.svg';
import ModalEle from 'common/components/Modal/ModalEle';
import { AI_SIMULATION_COPYPASTE_ERRORMSG } from 'common/constants/AISimulationConstants';

import FileDrop from 'features/DataIngestion/FileUpload/FileDrop';

import { ButtonComp } from '../ObligationAndControl/ObligationAndControlModules';

import { Loader } from './SimulationArenaModules';

import styles from './CopyAndPasteImportModal.module.scss';

const { LARGE_FILE, UPLOAD_FAIL, DOC_TYPE } = AI_SIMULATION_COPYPASTE_ERRORMSG;

interface IModelProps {
	openModal: boolean;
	handleClose: () => void;
	isLoading: boolean;
	isError?: any;
	handleSubmit: (data: File[]) => void;
}

const CopyAndPasteImportModal = (props: IModelProps) => {
	const { openModal, handleClose, handleSubmit, isLoading, isError } = props;
	const [fileData, setFileData] = useState<File[]>([]);
	const [, setfileWindowFlag] = useState(false);
	const [isFileDropLoading] = useState<boolean>(false);
	const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<
		string | undefined
	>(undefined);

	const handleModalClose = () => {
		handleClose();
		setFileData([]);
		setFileUploadErrorMessage(undefined);
	};

	const handleUpload = () => {
		handleSubmit(fileData);
		setFileData([]);
	};

	const handleFileData = useCallback((data: File[], errorMessage?: string) => {
		setFileData(data);
		setFileUploadErrorMessage(errorMessage);
	}, []);

	const hideFileDropHandler = () => {
		setfileWindowFlag(false);
		setFileData([]);
	};

	const deleteAttachment = () => {
		setFileData([]);
	};

	return (
		<ModalEle
			open={openModal}
			handleClose={handleModalClose}
			sxBoxProps={{ width: 'auto' }}
			data-testid="copy-paste-modal"
		>
			<Stack direction="column">
				<Stack direction="row" justifyContent="space-between">
					<Typography className={styles['header-text']} data-testid="header">
						Upload your file
					</Typography>
					<CloseIcon
						onClick={handleModalClose}
						className={styles['add-closemodal']}
						data-testid="close-icon"
					/>
				</Stack>
				<Typography className={styles['paragraph-text']} data-testid="header">
					You can upload PDF, DOC or DOCX files. The max file size is 10mb.
				</Typography>
			</Stack>
			<Grid
				className={styles['filedrop-padding']}
				justifyContent="space-between"
				direction="row"
			>
				<FileDrop
					accept={{
						'application/pdf': ['.pdf'],
						'application/msword': ['.doc', '.docx'],
					}}
					handleFileData={handleFileData}
					fileData={fileData}
					isLoading={isFileDropLoading}
					hideFileDrop={hideFileDropHandler}
				/>
				{fileData.length > 0 &&
					fileData.some((file) => file.size < 10 * 1024 * 1024) && (
						<GreenTick className={styles['notification-success']} />
					)}
			</Grid>
			{isLoading && <Loader show={isLoading} />}
			{!isLoading &&
				fileData.length > 0 &&
				fileData.some((file) => file.size > 10 * 1024 * 1024) && (
					<Typography className={styles['file-upload-error-message']}>
						{LARGE_FILE}
					</Typography>
				)}
			{isError && !isLoading && (
				<Typography className={styles['file-upload-error-message']}>
					{UPLOAD_FAIL}
				</Typography>
			)}
			{fileData.length > 0 && (
				<Grid
					container
					spacing={2}
					justifyContent="space-between"
					alignItems="center"
				>
					<Grid item>
						<Typography
							className={styles['delete-attachment']}
							onClick={deleteAttachment}
							data-testid="delete-attachment"
						>
							Delete Attachment
						</Typography>
					</Grid>
					<Grid item>
						<ButtonComp
							btnType="primary"
							disabled={
								isLoading ||
								fileData.some((file) => file.size > 10 * 1024 * 1024)
							}
							clickHandler={handleUpload}
							data-testid="addtestmodal-upload"
						>
							Upload
						</ButtonComp>
					</Grid>
				</Grid>
			)}
			{fileUploadErrorMessage && (
				<Grid
					container
					direction="row"
					className={styles['file-upload-error-container']}
				>
					<Grid item display="flex" gap="10px">
						<Delete />
						<Grid item xs zeroMinWidth>
							<Typography className={styles['file-upload-error-header']}>
								Alert
							</Typography>
							<Typography className={styles['file-upload-error-sub-header']}>
								{DOC_TYPE}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			)}
		</ModalEle>
	);
};

export default CopyAndPasteImportModal;
